import { createAsyncThunk } from "@reduxjs/toolkit";
import { captureException } from "@sentry/browser";

import * as basketAPI from "../../../api/basket";
import * as wishlistsAPI from "../../../api/wishlists";
import { IBasket, IWishListLine } from "../../../models/catalogue";

interface ReloadBasketResult {
    basket: IBasket | null;
    previouslyInBasket: readonly IWishListLine[];
}

/**
 * Reload the basket, with the previously-in-basket wishlist beside it.
 *
 * The server moves a removed line's product onto that list (and off it again
 * when it is added back), so both are fetched together and land in one
 * action, as they change together. A failed wishlist load is reported and
 * answered with an empty list rather than failing the basket.
 *
 * Basket state stays in the checkout slice, whose reducer applies this result
 * to `checkout.data.basket`, the wishlist, and the financing plans that come
 * inside the basket response.
 *
 * @sideEffects
 * - Makes API calls via basketAPI.load and wishlistsAPI.listLines
 * - Updates `data.basket`, `data.wishlists` and `data.financing_plans` via checkout's extraReducers
 */
export const reloadBasket = createAsyncThunk(
    "basket/reloadBasket",
    async (): Promise<ReloadBasketResult> => {
        const [basket, previouslyInBasket] = await Promise.all([
            basketAPI.load(),
            wishlistsAPI
                .listLines(wishlistsAPI.LIST_PREVIOUSLY_IN_BASKET)
                .catch((err: unknown) => {
                    captureException(err);
                    return [];
                }),
        ]);
        return { basket, previouslyInBasket };
    },
);
